// #3878 — OfflineAtStart re-probe gate for couch login.
//
// attemptCouchLogin_innerAsync bailed "OfflineAtStart" whenever the cached connection
// state read != Online. But that state comes from a HEAD /assets/imgs/ping.svg probe that
// can latch offline at cold start or on one transient failure even while the device is
// actually online (every OfflineAtStart event carried online_at_attempt: true).
// OfflineAtStart was the second-largest couch_login_failure bucket: 165 users/30d, spread
// across all browsers/OSes and web+native — i.e. probe reliability, not a real outage.
//
// Before bailing we now do a fresh reachability re-probe. If the server answers we proceed
// (the caller corrects the stale state with the re-entrancy-safe forceOnline()); only when
// it is genuinely unreachable do we still bail.
//
// Pure module (no Angular deps) for node unit tests — same pattern as couch-login-stuck.ts.

import { createTimeoutSignal } from './timeout-signal';
import { realTimerEnvironment, RunningTimeTimeoutHandle, scheduleRunningTimeTimeout, TimerEnvironment } from './running-clock';

// L3 (#3986): how long the reachability HEAD probe waits for the server to answer before
// it gives up and treats the network as unreachable. Without this bound a black-hole
// network (captive portal that swallows the request) hung the probe forever — and the
// probe is awaited inside cold couch-login and on every soft resume, so the whole boot /
// resume wedged. 10 s is long enough to ride out a slow-but-real network, short enough to
// fail fast on a black hole.
// Wall time, checked in #6096: the probe sends no report, so a fire after a freeze cannot be
// counted; and a request open across a freeze is better abandoned than waited on. Every
// resume probes again (#4318), so one abandoned on waking is retried at once.
// #6389: the ping is ALSO bounded by the same limit in running time, the clock the
// first-probe gate uses; see pingWithinProbeLimitAsync.
export const REACHABILITY_PROBE_TIMEOUT_MS = 10_000;

export type CouchLoginOnlineGate = 'proceed' | 'force-online' | 'bail';

export interface CouchLoginOnlineGateState {
  /** Does the cached connection state already read Online? */
  connectionStateOnline: boolean;
  /** Result of a fresh reachability re-probe — only consulted when not already Online. */
  reprobeReachable: boolean;
  /**
   * #6231: does the connection state read Online once the re-probe has answered? Only
   * consulted when the re-probe found the server unreachable.
   */
  connectionStateOnlineAfterReprobe: boolean;
}

/**
 * Decide whether couch login may proceed given the cached online state and a fresh re-probe.
 * - already Online            -> proceed (no re-probe needed)
 * - cached offline, reachable -> force-online (trust the fresh probe over the stale latch)
 * - cached offline, unreachable, but Online by the time the re-probe answered -> proceed
 *   (#6231: the re-probe can run 10 s. A newer check found the network while it ran, so its
 *   "unreachable" is the older answer. If the network really is still down, the login request
 *   itself fails with a network error, which every caller already treats as recoverable.)
 * - cached offline, unreachable, still offline -> bail (genuinely offline)
 */
export function resolveCouchLoginOnlineGate(state: CouchLoginOnlineGateState): CouchLoginOnlineGate {
  if (state.connectionStateOnline) return 'proceed';
  if (state.reprobeReachable) return 'force-online';
  return state.connectionStateOnlineAfterReprobe ? 'proceed' : 'bail';
}

/**
 * #6389: thrown when a reachability ping is ended by its RUNNING-time limit rather than by its
 * abort signal or the server. Its name is `TimeoutError`, like the signal's own timeout.
 */
export class ProbeRunningTimeLimitError extends Error {
  constructor(
    /** was the wall-time abort signal already fired? If so, the request ignored it */
    readonly signalAborted: boolean,
    /** how much of the limit the page spent frozen, as the running-time clock saw it */
    readonly notRunningMs: number,
  ) {
    super('Reachability ping ended by its running-time limit');
    this.name = 'TimeoutError';
  }
}

/** Makes the abort signal that ends a reachability ping after `timeoutMs` of wall time. */
export type CreateProbeSignal = (timeoutMs: number) => AbortSignal;

/**
 * Send the reachability HEAD ping, bounded by `timeoutMs` twice over: once on wall time by the
 * abort signal (#3986, kept as #6096 decided), and once on RUNNING time (#6389). Whichever comes
 * first ends the ping; the running-time one throws `ProbeRunningTimeLimitError`.
 *
 * Why the second limit: the first-probe gate that boot waits on counts running time, and credits
 * a freeze as up to 5 s in one tick. After a freeze its tick could come due before the signal's
 * timer and find no verdict, although its 12 s is longer than this 10 s. On the same clock this
 * limit's tick always comes due first. It also ends a request that ignores its signal. A page
 * that never froze has run for the whole wall time, so there the signal still decides.
 * design-docs/sync-integrity.md, "When the wait runs out (#6121)".
 *
 * Resolves with the server's response, whatever its status; rejects when the request fails or
 * either limit ends it. A request abandoned by the running-time limit may settle later; that
 * answer is ignored.
 */
export async function pingWithinProbeLimitAsync(
  fetchFn: typeof fetch,
  url: string,
  env: TimerEnvironment = realTimerEnvironment(),
  timeoutMs: number = REACHABILITY_PROBE_TIMEOUT_MS,
  createSignal: CreateProbeSignal = createTimeoutSignal,
): Promise<Response> {
  const signal = createSignal(timeoutMs);
  let limit: RunningTimeTimeoutHandle | undefined;
  const limitReached = new Promise<never>((_resolve, reject) => {
    limit = scheduleRunningTimeTimeout(
      () => reject(new ProbeRunningTimeLimitError(signal.aborted, limit?.notRunningMs() ?? 0)),
      timeoutMs,
      env,
    );
  });
  try {
    return await Promise.race([fetchFn(url, { method: 'HEAD', cache: 'no-cache', signal }), limitReached]);
  } finally {
    limit?.cancel();
  }
}

/**
 * Probe server reachability with a HEAD request. Resolves true if the server answers AT ALL
 * (even a non-2xx status means we reached it), false only if the fetch itself throws. Pure:
 * takes the fetch fn + url so it is node-testable and never mutates connection state (which
 * is what keeps it safe to call from inside attemptCouchLoginAsync — see the re-entrancy note
 * in online-state.service.ts).
 */
export async function fetchReachableAsync(
  fetchFn: typeof fetch,
  url: string,
  timeoutMs: number = REACHABILITY_PROBE_TIMEOUT_MS,
): Promise<boolean> {
  try {
    // L3 (#3986): bound the probe so a black-hole network can't hang it forever. A timed-out
    // probe throws into the catch below, so it is treated as unreachable, exactly like an
    // outright network error. #6389: bounded on running time as well as wall time.
    await pingWithinProbeLimitAsync(fetchFn, url, realTimerEnvironment(), timeoutMs);
    return true;
  } catch {
    // a throw — network error or timeout — IS the probe's answer: unreachable
    return false;
  }
}
